/**
 * PAPPO — Navbar (barra superior)
 * Logótipo + área do utilizador (alternador de posição da navegação, tema,
 * sino de notificações, avatar e sair). Os LINKS de navegação vivem agora na
 * MainNav (trilha lateral no PC / barra inferior no celular).
 */

import { useNavigate } from 'react-router-dom';
import { LogOut, Bell, PanelLeft, PanelBottom } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../contexts/NotificationsContext';
import { Avatar } from '../common/Avatar';
import { ThemeSelector } from '../common/ThemeSelector';
import { useTheme } from '../../contexts/ThemeContext';
import type { NavPos } from '../../hooks/useNavLayout';
import styles from './Navbar.module.css';

/* Logo por tema: a marca acompanha a cor do tema ativo (roxa nos temas roxos,
   vermelha nos vermelhos, azul no gélido). Fallback = roxa original. */
const LOGO_BY_THEME: Record<string, string> = {
  'dark-red': '/pappo-logo-red.png',
  'light-red': '/pappo-logo-red.png',
  'ice-blue': '/pappo-logo-blue.png',
};

interface NavbarProps {
  navPos: NavPos;
  setNavPos: (pos: NavPos) => void;
  showToggle: boolean;
}

export function Navbar({ navPos, setNavPos, showToggle }: NavbarProps) {
  const { user, logout } = useAuth();
  const { total } = useNotifications();
  const { themeName } = useTheme();
  const navigate = useNavigate();

  const logoSrc = LOGO_BY_THEME[themeName] ?? '/pappo-logo.png';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <nav className={styles.navbar} role="navigation" aria-label="Barra superior">
      {/* Logo */}
      <div className={styles.brand}>
        <img src={logoSrc} alt="Pappo" className={styles.logoImg} />
      </div>

      <div className={styles.spacer} />

      {/* User area */}
      <div className={styles.userArea}>
        {showToggle && (
          <button
            className={styles.logoutBtn}
            onClick={() => setNavPos(navPos === 'lateral' ? 'inferior' : 'lateral')}
            aria-label="Alternar posição da barra de navegação"
            title={
              navPos === 'lateral'
                ? 'Mover a barra para baixo'
                : 'Mover a barra para a lateral'
            }
          >
            {navPos === 'lateral' ? <PanelBottom size={18} /> : <PanelLeft size={18} />}
          </button>
        )}
        <ThemeSelector />
        {user && total > 0 && (
          <div
            className={styles.bellWrap}
            title={`${total} notificações`}
            aria-label={`${total} notificações`}
          >
            <Bell size={18} />
            <span className={styles.bellBadge}>{total > 99 ? '99+' : total}</span>
          </div>
        )}
        {user && (
          <>
            <Avatar name={user.display_name} src={user.avatar_url} size="sm" />
            <span className={styles.userName}>{user.display_name}</span>
            <button
              className={styles.logoutBtn}
              onClick={handleLogout}
              aria-label="Sair da conta"
              title="Sair"
            >
              <LogOut size={18} />
            </button>
          </>
        )}
      </div>
    </nav>
  );
}
